import { useEffect } from 'react'
import { ModalCloseButton } from '@/components/ModalCloseButton'

type ConfirmationDialogProps = {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  cancelLabel: string
  closeLabel?: string
  confirmDisabled?: boolean
  confirmPending?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  closeLabel = cancelLabel,
  confirmDisabled = false,
  confirmPending = false,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps) {
  useEffect(() => {
    if (!open) return

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel()
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onCancel, open])

  if (!open) return null

  return (
    <div className="confirmation-backdrop" onClick={onCancel}>
      <section
        className="confirmation-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="confirmation-dialog-icon" aria-hidden="true">!</div>
        <div className="min-w-0 pr-8">
          <h2 id="confirmation-dialog-title">{title}</h2>
          <p id="confirmation-dialog-description">{description}</p>
        </div>
        <ModalCloseButton
          onClick={onCancel}
          label={closeLabel}
          className="absolute right-4 top-4 h-8 w-8"
        />
        <div className="confirmation-dialog-actions">
          <button
            className="confirmation-dialog-button inline-flex min-h-[42px] items-center justify-center rounded-xl border border-white/[0.1] bg-[#111719] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#171e20] disabled:cursor-not-allowed disabled:opacity-45"
            type="button"
            onClick={onCancel}
            disabled={confirmPending}
          >
            {cancelLabel}
          </button>
          <button
            className="confirmation-dialog-button inline-flex min-h-[42px] items-center justify-center rounded-xl bg-accent-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-45"
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled || confirmPending}
          >
            {confirmPending ? `${confirmLabel}…` : confirmLabel}
          </button>
        </div>
      </section>
    </div>
  )
}
